import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from '@mui/material';

import RestUtilities from '../../services/RestUtilities';
import { Booking } from '../../@types/api';
import DeleteIcon from '@mui/icons-material/Delete';
import { roundToCurrencyAsString } from '../../Functions';
import { useState } from 'react';
import dayjs from 'dayjs';
import { useSetRecoilState } from 'recoil';
import { toastState } from '../../state/RecoilState';

const paymentTypes = {
  BANKTRANSFER: 3,
  CHEQUE: 1,
  CASH: 4,
};

const tableContainer = {
  marginBottom: 1,
  marginTop: 1,
};

interface Props {
  id: string | undefined;
  booking: Booking | undefined;
  open: boolean;
  setOpen: (value: boolean) => void;
  paymentBookingFeeId: number | undefined;
  setPaymentBookingFeeId: (value: number) => void;
  paymentMethodId: 1 | 3 | 4;
  setPaymentMethodId: (value: 1 | 3 | 4) => void;
  paymentAmount: number;
  setPaymentAmount: (value: number) => void;
  setBookingSafely: (value: Booking) => void;
}

const PaymentsDialogue: React.FC<Props> = ({
  id,
  booking,
  paymentBookingFeeId,
  paymentMethodId,
  paymentAmount,
  open,
  setOpen,
  setPaymentBookingFeeId,
  setPaymentMethodId,
  setPaymentAmount,
  setBookingSafely,
}) => {
  const setToast = useSetRecoilState(toastState);
  const theme = useTheme();
  const [notifyClient, setNotifyClient] = useState(true);

  const formControlModal = {
    width: '40%',
    margin: theme.spacing(1, 2),
  };

  const handleAddPayment = async () => {
    let res;
    if (!booking) return;
    const newPayment = {
      bookingId: id,
      bookingFeeId: paymentBookingFeeId,
      feeTypeId: booking.bookingFees?.find(
        (x) => x.bookingFeeId === paymentBookingFeeId
      ).feeTypeId,
      paymentMethodId,
      paymentAmount,
    };
    res = await RestUtilities.post(
      `/api/booking/addPayment?notifyClient=${notifyClient}`,
      newPayment
    );
    if (res?.ok) {
      setBookingSafely(res.content);
      setOpen(false);
      setToast(
        `Payment added ${
          notifyClient ? 'and client emailed' : 'but not emailed'
        }`
      );
    }
  };

  const deletePayment = async (payment: any) => {
    const res = await RestUtilities.delete(
      `/api/booking/deletePayment/${payment.feePaymentId}`
    );
    if (res.ok) {
      setBookingSafely(res.content);
      setToast('Payment deleted');
    }
  };

  const emailReceipt = async (feePaymentId: number) => {
    await RestUtilities.get(
      `/api/booking/ResendReceipt?feePaymentId=${feePaymentId}&clientId=${booking?.clientId}`
    );
    setToast('Receipt emailed');
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotifyClient(event.target.checked);
  };

  if (!booking) return null;

  return (
    <>
      <TableContainer component={Paper} sx={tableContainer}>
        <Table aria-label='payment table' size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>FeeType</TableCell>
              <TableCell align='right'>PaymentDate</TableCell>
              <TableCell align='right'>Amount</TableCell>
              <TableCell align='right'>Method</TableCell>
              <TableCell align='right'></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {booking.feePayments?.map((item) => (
              <TableRow key={item.bookingFeeId}>
                <TableCell>
                  <Button
                    size='small'
                    color='primary'
                    variant='contained'
                    sx={{ p: 0 }}
                    onClick={() => emailReceipt(item.feePaymentId)}
                  >
                    Send
                  </Button>
                </TableCell>
                <TableCell>
                  {item.bookingFee?.feeType?.feeTypeDescription}
                </TableCell>
                <TableCell align='right'>
                  {dayjs(item.paymentDate).format('DD MMM YYYY')}
                </TableCell>
                <TableCell align='right'>{item.paymentAmount}</TableCell>
                <TableCell align='right'>
                  {item.paymentMethod.paymentMethodDescription}
                </TableCell>
                <TableCell align='right'>
                  <IconButton
                    aria-label='delete'
                    onClick={(e) => deletePayment(item)}
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Add Payment</DialogTitle>
        <DialogContent>
          <FormControl sx={formControlModal}>
            <InputLabel id='paymentType-label'>Fee Type</InputLabel>
            <Select
              labelId='paymentType-label'
              label='Fee Type'
              value={paymentBookingFeeId}
              size='small'
              onChange={(event) => {
                const pbfId = Number(event.target.value);
                const bFee = booking.bookingFees?.find(
                  (x) => x.bookingFeeId === pbfId
                );
                const defaultAmount = roundToCurrencyAsString(
                  Number(bFee.feeAmount) * (1 + bFee.rate.rateValue)
                );
                setPaymentBookingFeeId(pbfId);
                setPaymentAmount(Number(defaultAmount));
              }}
            >
              <MenuItem value={-1}>Please Select the Fee</MenuItem>
              {booking?.bookingFees?.map((item) => (
                <MenuItem key={item.bookingFeeId} value={item.bookingFeeId}>
                  {item.feeType.feeTypeDescription}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={formControlModal}>
            <TextField
              required
              size='small'
              id='paymentAmount'
              label='PaymentAmount'
              value={paymentAmount}
              onChange={(event) => {
                setPaymentAmount(Number(event.target.value));
              }}
            />
          </FormControl>
          <FormControl sx={formControlModal}>
            <InputLabel id='paymentMethod-label'>Payment Method</InputLabel>
            <Select
              labelId='paymentMethod-label'
              label='Payment Method'
              value={paymentMethodId}
              onChange={(event) =>
                setPaymentMethodId(Number(event.target.value) as 1 | 3 | 4)
              }
              size='small'
            >
              <MenuItem value={paymentTypes.CHEQUE}>Cheque</MenuItem>
              <MenuItem value={paymentTypes.BANKTRANSFER}>
                Bank Transfer
              </MenuItem>
              <MenuItem value={paymentTypes.CASH}>Cash</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={formControlModal}>
            <FormControlLabel
              control={
                <Switch
                  checked={notifyClient}
                  onChange={handleChange}
                  name='gilad'
                />
              }
              label='Send Notification'
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color='primary'>
            Cancel
          </Button>
          <Button onClick={handleAddPayment} color='primary'>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default PaymentsDialogue;
